import Component from '@glimmer/component';
import { service } from '@ember/service';
import { hash, array } from '@ember/helper';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { formatNumber } from 'ember-intl';
import { eq, isEmpty, or, and, not } from 'ember-truth-helpers';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
import {
  HdsCardContainer,
  HdsIconTile,
  HdsTextDisplay,
  HdsLinkStandalone,
  HdsIcon,
} from '@hashicorp/design-system-components/components';
import { Flex, Loading, Time, DateRange } from 'core/components';
import { classNames } from 'core/helpers';
import linkToTfcResource from 'core/helpers/link-to-tfc-resource';
import { boxMargin, boxPadding, tooltip } from 'core/modifiers';
import { GEOS } from 'core/utils/constants';
import ContactSales from 'billing-common/components/billing/contact-sales';
import statusForTooltip from 'billing-common/helpers/status-for-tooltip';
import PaymentBanner from 'billing-common/components/billing/payment-banner';

/**
 * A billing model.
 * @argument billing
 * @type {number}
 */

/**
 * The organization in context.
 * @argument organization
 * @type {Object}
 */

/**
 * The projects associated with the organization.
 * @argument projects
 * @type {Array}
 */

/**
 * The running statement for billing.
 * @argument runningStatement
 * @type {object}
 */
/**
 *
 * `PageOrganizationsDetailBillingCard` is a card for the organization
 * dashboard that renders billing data and a link to the billing page.
 *
 *
 * ```
 * <Page::Organizations::Detail::BillingCard
 *   @billing={{@billing}}
 *   @organization={{@organization}}
 *   @projects-{{@projects}}
 *   @runningStatement={{@runningStatement}}
 * />
 * ```
 *
 * @class PageOrganizationsDetailBillingCard
 *
 */
export default class PageOrganizationsDetailBillingCard extends Component {
  @service billing;
  @service systemStatus;
  @service userContext;
  @service config;

  constructor() {
    super(...arguments);

    if (!this.userContext.billing) {
      this.setup.perform();
    }
  }

  get GEOS() {
    return GEOS;
  }

  get deployGeo() {
    return this.config?.app?.deployGeo ?? this.GEOS.US;
  }

  get statusPageUrl() {
    return this.systemStatus.statusPageUrl;
  }

  get showLoading() {
    return this.setup.isRunning || !this.userContext.billing;
  }

  setup = task(async () => {
    const { organization, projects } = this.args;
    if (organization && projects && projects.length > 0) {
      this.userContext.billing = await this.billing.getBilling(
        organization.id,
        projects[0].id
      );
    }
  });
  <template>
    <HdsCardContainer
      @level="mid"
      @hasBorder="true"
      class="hcp-flex billing-card"
      ...attributes
      {{boxPadding "lg"}}
      {{didInsert this.setup.perform}}
    >
      <Flex @direction="column" @justifyContent="space-between" @grow={{1}}>
        <Flex @direction="column" @grow={{1}} {{boxMargin "0 0 lg"}}>
          <Flex
            @alignItems="center"
            @direction="row"
            @gap="md"
            {{boxMargin "0 0 lg"}}
          >
            <HdsIconTile @icon="credit-card" />
            <HdsTextDisplay
              @size="400"
              @weight="bold"
              @tag="h2"
              data-test-organization-dashboard-billing-header
            >
              {{t
                "components.page.organizations.detail.billing-card.header"
                geo=this.deployGeo
              }}
            </HdsTextDisplay>
          </Flex>
          {{#if this.showLoading}}
            <Loading data-test-loading as |L|>
              <L.Message>
                {{t
                  "components.page.organizations.detail.billing-card.loading"
                }}
              </L.Message>
            </Loading>
          {{else if @billing.billingAccount}}
            {{#if (eq this.deployGeo this.GEOS.EU)}}
              <Flex
                @direction="column"
                @gap="md"
                data-test-organization-dashboard-billing-type-eu
              >
                <Flex @direction="column" @gap="lg">
                  <ContactSales
                    data-test-organization-dashboard-billing-type-eu-contact-sales
                  />
                  <Flex @direction="column" @gap="sm">
                    <HdsTextDisplay
                      @size="300"
                      @tag="h3"
                      data-test-organization-dashboard-billing-type-eu-title
                    >
                      {{t "billing-common.common.eu.usage.title"}}
                    </HdsTextDisplay>
                    <HdsLinkStandalone
                      @href={{linkToTfcResource
                        this.deployGeo
                        "hashicorp.terraform_cloud.organization:usage"
                        @organization.name
                      }}
                      @text={{t "billing-common.common.eu.usage.link-text"}}
                      @icon="external-link"
                      @iconPosition="trailing"
                      @isHrefExternal={{true}}
                      data-test-organization-dashboard-billing-type-eu-tfc-usage-link
                    />
                  </Flex>
                </Flex>
              </Flex>
            {{else if @billing.hasContractPayment}}
              <Flex
                @direction="column"
                data-test-organization-dashboard-billing-type-contract
              >
                <HdsTextDisplay
                  @size="300"
                  @tag="h3"
                  data-test-organization-dashboard-billing-type-contract-header
                >
                  {{t
                    "components.page.organizations.detail.billing-card.account-type"
                  }}
                </HdsTextDisplay>
                <Flex @direction="column" @gap="md">
                  <p
                    class={{classNames
                      "hds-typography-display-300"
                      "hds-font-weight-medium"
                      "hds-foreground-primary"
                    }}
                    data-test-organization-dashboard-billing-type-contract-subtitle
                  >
                    {{t
                      "components.page.organizations.detail.billing-card.annual-contract"
                    }}
                  </p>
                  <p
                    class="hds-typography-body-200"
                    data-test-organization-dashboard-billing-type-contract-message
                  >
                    {{t
                      "components.page.organizations.detail.billing-card.annual-contract-description"
                    }}
                  </p>
                </Flex>
              </Flex>
            {{else if @billing.hasFlexPayment}}
              <Flex
                @direction="column"
                @gap="md"
                data-test-organization-dashboard-billing-type-flex
              >
                <Flex @direction="column" @gap="2xs">
                  <Flex
                    @alignItems="center"
                    @justifyContent="space-between"
                    @gap="2xs"
                  >
                    <Flex @alignItems="center" @direction="row" @gap="2xs">
                      <HdsTextDisplay
                        @size="300"
                        @tag="h3"
                        data-test-organization-dashboard-billing-type-header
                      >
                        {{t
                          "components.page.organizations.detail.billing-card.available-balance"
                        }}
                      </HdsTextDisplay>
                      <HdsIcon
                        @name="info"
                        @color="var(--token-color-palette-blue-200)"
                        @isInline={{true}}
                        {{tooltip
                          (t
                            (statusForTooltip
                              @billing.flex.latestContract.status
                              @billing.flex.depletedUpcomingRenewal
                              (if
                                (isEmpty @billing.flex.upcomingContract)
                                @billing.flex.balance.estimatedRemainingBalance
                              )
                            )
                          )
                          options=(hash placement="bottom" offset=(array 0 10))
                        }}
                        data-test-organization-dashboard-billing-type-icon
                      />
                    </Flex>
                    <p
                      class={{classNames
                        "hds-typography-display-300"
                        "hds-font-weight-medium"
                        "hds-foreground-strong"
                        (if
                          @billing.flex.depletedNoRenewal
                          "hds-foreground-critical-on-surface"
                        )
                      }}
                      data-test-organization-dashboard-billing-type-available-balance
                    >
                      {{formatNumber
                        @billing.flex.balance.estimatedRemainingBalance
                        style="currency"
                        currency="USD"
                      }}
                    </p>
                  </Flex>
                  <p class="hds-typography-body-200 hds-foreground-faint">
                    <span
                      data-test-organization-dashboard-billing-type-last-updated
                    >
                      {{t
                        "components.page.organizations.detail.billing-card.last-updated"
                      }}
                    </span>
                    <Time
                      @date={{@billing.flex.latestContract.updatedAt}}
                      @display="friendly-local"
                      data-test-organization-dashboard-billing-type-date={{@billing.flex.latestContract.updatedAt}}
                    />
                  </p>
                </Flex>
                <Flex @direction="column" @gap="2xs">
                  <Flex
                    @alignItems="center"
                    @justifyContent="space-between"
                    @gap="2xs"
                  >
                    <HdsTextDisplay
                      @size="300"
                      @tag="h3"
                      data-test-organization-dashboard-billing-type-usage
                    >
                      {{t
                        "components.page.organizations.detail.billing-card.usage"
                      }}
                    </HdsTextDisplay>
                    {{#if @runningStatement}}
                      <p
                        class={{classNames
                          "hds-typography-display-300"
                          "hds-font-weight-medium"
                          "hds-foreground-strong"
                        }}
                        data-test-organization-dashboard-billing-type-flex-usage-total
                      >
                        {{formatNumber
                          @runningStatement.total
                          style="currency"
                          currency="USD"
                        }}
                      </p>
                    {{else}}
                      <p
                        class="hds-typography-body-200 hds-font-weight-regular"
                        data-test-organization-dashboard-billing-type-flex-zero-usage
                      >
                        {{t
                          "components.page.organizations.detail.billing-card.zero-usage"
                        }}
                      </p>
                    {{/if}}
                  </Flex>
                  {{#if @runningStatement}}
                    <p
                      class="hds-typography-body-200 hds-foreground-faint"
                      data-test-organization-dashboard-billing-type-flex-usage-range
                    >
                      <DateRange
                        @startDate={{@runningStatement.billingPeriodStart}}
                        @endDate={{@runningStatement.billingPeriodEnd}}
                        @condensed={{true}}
                        @asUTC={{true}}
                      />
                    </p>
                  {{/if}}
                </Flex>
              </Flex>
            {{else}}
              <Flex
                @direction="column"
                @gap="md"
                data-test-organization-dashboard-billing-type-other
              >
                <Flex @direction="column" @gap="2xs">
                  <HdsTextDisplay @size="300" @tag="h3">
                    {{t
                      "components.page.organizations.detail.billing-card.usage"
                    }}

                  </HdsTextDisplay>
                  {{#if @runningStatement}}
                    <p
                      class={{classNames
                        "hds-typography-display-300"
                        "hds-font-weight-medium"
                        "hds-foreground-primary"
                      }}
                      data-test-organization-dashboard-billing-type-other-usage
                    >
                      {{formatNumber
                        @runningStatement.total
                        style="currency"
                        currency="USD"
                      }}
                    </p>
                  {{else}}
                    <p
                      class="hds-typography-body-200 hds-font-weight-regular"
                      data-test-organization-dashboard-billing-type-other-zero-usage
                    >
                      {{t
                        "components.page.organizations.detail.billing-card.zero-usage"
                      }}
                    </p>
                  {{/if}}
                </Flex>
                <PaymentBanner
                  @accountId={{@billing.billingAccount.id}}
                  @accountStatus={{@billing.status}}
                  @cardDetails={{@billing.onDemand.cardDetails}}
                  @credits={{@billing.credits}}
                  @editPaymentRoute="billing.accounts.account.payments.credit-card"
                  @isRemovingCreditCard={{@model.onDemand.status.isRemovingCreditCard}}
                  @hasCardError={{@billing.onDemand.hasCardError}}
                  @isOnContract={{or
                    @billing.hasFlexPayment
                    @billing.hasContractPayment
                  }}
                >
                  <:or>
                    {{#if (and @billing.credits (not @billing.credits.isZero))}}
                      <Flex @alignItems="start" @direction="row" @gap="xs">
                        <div>
                          <HdsIcon
                            @name="info"
                            @color="var(--token-color-palette-blue-200)"
                            @isInline={{true}}
                          />
                        </div>
                        <p class="hds-typography-body-200">
                          {{t
                            "components.page.organizations.detail.billing-card.credit-info"
                            credits=(formatNumber
                              @billing.credits style="currency" currency="USD"
                            )
                          }}
                        </p>
                      </Flex>
                    {{/if}}
                  </:or>
                </PaymentBanner>
              </Flex>
            {{/if}}
          {{else}}
            {{! TODO: update to display billing unavailable error in follow up }}
            <Loading data-test-loading as |L|>
              <L.Message>
                {{t
                  "components.page.organizations.detail.billing-card.loading"
                }}
              </L.Message>
            </Loading>
          {{/if}}
        </Flex>
        {{#if
          (and
            @billing.billingAccount
            (not this.showLoading)
            (eq this.deployGeo this.GEOS.US)
          )
        }}
          <Flex>
            <HdsLinkStandalone
              @icon="arrow-right"
              @iconPosition="trailing"
              @model={{@billing.billingAccount.id}}
              @query={{hash org_id=@billing.billingAccount.organizationId}}
              @route="billing.accounts.account"
              @text={{t
                "components.page.organizations.detail.billing-card.view-billing"
              }}
              data-test-organization-dashboard-billing-link
            />
          </Flex>
        {{/if}}
      </Flex>
    </HdsCardContainer>
  </template>
}
